import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Briefcase,
  MapPin,
  Clock,
  Building,
  Send,
  CheckCircle,
  AlertCircle,
  Upload,
  FileText,
  X,
} from "lucide-react";
import axios from "axios";

// Create a public API instance without auth interceptors
const API_URL =
  import.meta.env.VITE_API_URL ||
  "https://srv1309580.hstgr.cloud/puffin/api/v1";
const publicApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

const PublicJobApplication = () => {
  const { publicLink } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    cv_link: "",
    cover_letter: "",
  });

  useEffect(() => {
    fetchJob();
  }, [publicLink]);

  const fetchJob = async () => {
    try {
      const response = await publicApi.get(
        `/hr/recruitment/public/jobs/${publicLink}`,
      );
      setJob(response.data?.data?.job || response.data?.job);
    } catch (err) {
      console.error("Error fetching job:", err);
      setError("Job not found or no longer available");
    } finally {
      setLoading(false);
    }
  };

  const handleCvUpload = async (file) => {
    if (!file) return null;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Please upload a PDF or Word document");
      return null;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return null;
    }

    try {
      setCvUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await publicApi.post(
        "/hr/recruitment/public/upload-cv",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.data.success) {
        return response.data.data.url;
      }
      throw new Error("Upload failed");
    } catch (err) {
      console.error("CV upload error:", err);
      setError("Failed to upload CV. Please try again.");
      return null;
    } finally {
      setCvUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      let cvLink = form.cv_link;

      if (cvFile) {
        cvLink = await handleCvUpload(cvFile);
        if (!cvLink) {
          setSubmitting(false);
          return;
        }
      }

      if (!cvLink) {
        setError("Please upload your CV or provide a link");
        setSubmitting(false);
        return;
      }

      await publicApi.post(`/hr/recruitment/public/jobs/${publicLink}/apply`, {
        ...form,
        cv_link: cvLink,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Job Not Found
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Application Submitted!
          </h1>
          <p className="text-gray-600">
            Thank you for applying. We'll be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Job Details */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Briefcase className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{job?.title}</h1>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                {job?.department?.name && (
                  <span className="flex items-center gap-1">
                    <Building className="h-4 w-4" /> {job.department.name}
                  </span>
                )}
                {job?.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" /> {job.location}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />{" "}
                  {job?.job_type?.replace("_", " ")}
                </span>
              </div>
            </div>
          </div>
          {job?.description && (
            <div className="prose max-w-none">
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">
                {job.description}
              </p>
            </div>
          )}
        </div>

        {/* Application Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Apply for this position</h2>
          {error && <p className="text-red-600 mb-4">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={form.first_name}
                  onChange={(e) =>
                    setForm({ ...form, first_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={form.last_name}
                  onChange={(e) =>
                    setForm({ ...form, last_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                CV/Resume *
              </label>
              <div className="space-y-3">
                {/* File Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => {
                      setCvFile(e.target.files[0]);
                      setForm({ ...form, cv_link: "" });
                    }}
                    className="hidden"
                    id="cv-upload"
                  />
                  <label htmlFor="cv-upload" className="cursor-pointer">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      Click to upload PDF or Word document
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Max 10MB</p>
                  </label>
                </div>

                {cvFile && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <FileText className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700 flex-1 truncate">
                      {cvFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCvFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {cvUploading && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm">Uploading CV...</span>
                  </div>
                )}

                {/* Or link option */}
                {!cvFile && (
                  <>
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <div className="flex-1 border-t"></div>
                      <span>or paste a link</span>
                      <div className="flex-1 border-t"></div>
                    </div>
                    <input
                      type="url"
                      value={form.cv_link}
                      onChange={(e) =>
                        setForm({ ...form, cv_link: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="https://drive.google.com/..."
                    />
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Cover Letter
              </label>
              <textarea
                rows={4}
                value={form.cover_letter}
                onChange={(e) =>
                  setForm({ ...form, cover_letter: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Tell us why you're interested in this position..."
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Send className="h-4 w-4" />
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicJobApplication;
